import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import type { OfferableSlot, OfferableSlotsResponse } from '@vagusx/clients';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_1_MIN } from '@/lib/query-client';

/** The most slots the offerable-slots endpoint returns for one request. */
export const OFFERABLE_SLOTS_REQUEST_MAX = 500;

/**
 * Days per request. A week of one provider's times is almost always under the
 * endpoint's 500; a week that comes back full is asked again a day at a time,
 * and no one day comes near it.
 */
const CHUNK_DAYS = 7;

export interface OfferedSlotsRangeParams {
  providerId: string;
  appointmentTypeId: string;
  locationId: string;
  /** YYYY-MM-DD, inclusive. */
  startDate: string;
  /** How many days from `startDate`, inclusive of both ends' days. */
  days: number;
  isNewPatient: boolean;
}

export interface OfferedSlotsRange {
  /** Every offered slot in the range, earliest first. */
  slots: OfferableSlot[];
  /**
   * True only when a single day returned the endpoint's maximum, so that day
   * may hold more times than shown. A full week is split into days instead.
   */
  truncated: boolean;
}

/** The inclusive [from, to] day windows a range is asked for in, `size` days each. */
export function chunkRange(
  startDate: string,
  days: number,
  size: number = CHUNK_DAYS
): Array<{ from: string; to: string }> {
  const start = parseISO(startDate);
  const chunks: Array<{ from: string; to: string }> = [];
  for (let offset = 0; offset < days; offset += size) {
    const last = Math.min(offset + size, days) - 1;
    chunks.push({
      from: format(addDays(start, offset), 'yyyy-MM-dd'),
      to: format(addDays(start, last), 'yyyy-MM-dd'),
    });
  }
  return chunks;
}

/** Days from `from` to `to`, both included. */
function daysBetween(from: string, to: string): number {
  return Math.round((parseISO(to).getTime() - parseISO(from).getTime()) / 86_400_000) + 1;
}

/**
 * The slots the scheduling agent would offer one provider + type + location
 * over any number of days, with no cap on how many: asked for a week at a
 * time, in parallel, through the same offerable-slots engine the voice agent
 * uses. A week that comes back with the endpoint's maximum may have been cut
 * short, so it is asked again a day at a time. One cache entry for the range.
 */
export function useOfferedSlotsRange(
  params: OfferedSlotsRangeParams,
  enabled: boolean
): UseQueryResult<OfferedSlotsRange> {
  const ready = enabled && !!params.providerId && !!params.appointmentTypeId && !!params.locationId;

  return useQuery<OfferedSlotsRange>({
    queryKey: queryKeys.appointments.offerableSlots({ ...params, range: 'by-days' }),
    queryFn: async () => {
      const client = getAppointmentClient();
      const ask = (from: string, to: string): Promise<OfferableSlotsResponse> =>
        client.getOfferableSlots({
          providerId: params.providerId,
          locationId: params.locationId,
          appointmentTypeId: params.appointmentTypeId,
          fromDate: from,
          toDate: to,
          maxSlots: OFFERABLE_SLOTS_REQUEST_MAX,
          isNewPatient: params.isNewPatient,
          sortBy: 'earliest',
        });
      const full = (page: OfferableSlotsResponse): boolean =>
        page.slots.length >= OFFERABLE_SLOTS_REQUEST_MAX;

      let truncated = false;
      const pages = await Promise.all(
        chunkRange(params.startDate, params.days).map(async ({ from, to }) => {
          const page = await ask(from, to);
          if (!full(page)) return [page];
          // Possibly cut short: ask again for each day of the week.
          const days = await Promise.all(
            chunkRange(from, daysBetween(from, to), 1).map((d) => ask(d.from, d.to))
          );
          if (days.some(full)) truncated = true;
          return days;
        })
      );
      const seen = new Set<string>();
      const slots = pages
        .flat()
        .flatMap((p) => p.slots)
        .filter((slot) => (seen.has(slot.slotId) ? false : (seen.add(slot.slotId), true)))
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      return { slots, truncated };
    },
    enabled: ready,
    staleTime: CACHE_TIME_1_MIN,
  });
}
